import { CalendarRepresentation } from "./CalendarRepresentation.ts";
import { Interval } from "./Interval.tsx";
import { useCallback, useEffect, useState } from "react";
import { HourRepresentation } from "./HourRepresentation.ts";
import { CalendarItemMap } from "./CalendarItemMap.ts";

const MIN_START_HOUR = 7;
const MAX_END_HOUR = 22; // inclusive: last shown slot is 22:00–23:00

function createHours<T>(items: CalendarItemMap<T>): HourRepresentation[] {
  const range = items.getHourRange();
  const fromHour = range ? Math.min(MIN_START_HOUR, range.min) : MIN_START_HOUR;
  const toHour = range ? Math.max(MAX_END_HOUR, range.max) : MAX_END_HOUR;
  return Array.from(
    { length: toHour - fromHour + 1 },
    (_, i) => new HourRepresentation(fromHour + i, fromHour + i + 1),
  );
}
export function useCalendar<T>({
  interval,
  items,
  onChangeInterval,
}: {
  interval: Interval;
  items: CalendarItemMap<T>;
  onChangeInterval: (interval: Interval) => void;
}) {
  const [calendar, setCalendar] = useState<CalendarRepresentation<T>>(
    new CalendarRepresentation(
      interval.getDays(),
      createHours(items),
      items,
      interval,
    ),
  );

  const goNextPeriod = useCallback(() => {
    const newInterval = calendar.getInterval().nextPeriod();
    onChangeInterval(newInterval);
  }, [calendar, onChangeInterval]);
  const goPrevPeriod = useCallback(() => {
    const newInterval = calendar.getInterval().prevPeriod();
    onChangeInterval(newInterval);
  }, [calendar, onChangeInterval]);

  useEffect(() => {
    setCalendar(
      new CalendarRepresentation(
        interval.getDays(),
        createHours(items),
        items,
        interval,
      ),
    );
  }, [items, interval]);

  return {
    calendar,
    goNextPeriod,
    goPrevPeriod,
  };
}
